import React, { useState, useEffect, useContext } from "react";
import useFetch from "../../../hooks/fetchMSSQL-hook";
import { ValueSalesChart } from "./components/charts/ValueSalesChart";
import YearToYearMachinesSalesBar from "./components/charts/YearToYearMachinesSalesBar";
import TerminalsInfo from "./components/TerminalsInfo";
import YearToYearMachinesSalesBarFilter from "./components/filters/YearToYearBarMachinesSalesFilter";
import validFilter from "./utils/validFilter";
import { NavigationContext } from "../../../context/navigation-context";
import FilteredMachineSales from "./components/modals/FilteredMachineSalesV2";
import MachinesSalesCharts from "./components/charts/MachinesSalesCharts";
import ProductsSalesCharts from "./components/charts/ProductsSalesCharts";
import YearMachinesSalesChart from "./components/charts/YearMachinesSalesChart";
import YearProductsSalesChart from "./components/charts/YearProductsSalesChart";
import YearToYearProductsSalesBarFilter from "./components/filters/YearToYearBarProductsSalesFilter";
import getDefaultFilters from "./utils/getDefaultFilters";
import YearToYearProductsSalesBar from "./components/charts/YearToYearProductsSalesBar";
import SalesByDayChart from "./components/charts/SalesByDayChart";
import ZeroSalesChart from "./components/charts/ZeroSalesChart.js";
import UnassignedChoicesChart from "./components/charts/UnassignedChoicesChart.js";
import ProductPriceDeviationsChart from "./components/charts/ProductPriceDeviationsChart.js";

const chartTypes = [
  { name: "Sprzedaż", value: "CurrentSales" },
  { name: "Sprzedaż dzienna", value: "SalesByDay" },
  { name: "Sprzedaż (automaty)", value: "MachinesSalesCharts" },
  { name: "Sprzedaż (produkty)", value: "ProductsSalesCharts" },
  { name: "Sprzedaż roczna (automaty)", value: "YearMachinesSalesChart" },
  { name: "Sprzedaż roczna (produkty)", value: "YearProductsSalesChart" },
  { name: "Rok do roku (automaty)", value: "YearToYearMachinesSalesBar" },
  { name: "Rok do roku (produkty)", value: "YearToYearProductsSalesBar" },
  { name: "Brak sprzedaży", value: "ZeroSales" },
  { name: "Nieprzypisane wybory", value: "UnassignedChoices" },
  { name: "Odchylenia cen sprzedaży", value: "ProductPriceDeviations" },
];
export default function StatView() {
  const { fetchMssqlApi } = useFetch();
  const [terminalsActivity, setTerminalsActivity] = useState(null);
  const [yearToYearMachinesSales, setYearToYearMachinesSales] = useState([]);
  const [yearToYearProductsSales, setYearToYearProductsSales] = useState([]);
  const [machinesList, setMachinesList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [currentChartType, setCurrentChartType] = useState("CurrentSales");

  const hideModal = () => setShowModal(false);

  const getMachineSerialNo = ({ machineName }) => {
    if (!machineName || !machinesList.length) return null;

    const machine = machinesList.find(
      ({ Name, SerialNo: sn }) => `${Name} (${sn})` === machineName
    );
    return machine && machine.SerialNo || null;
  };

  const getEAN = ({ productName }) => {
    if (!productName) return null;
    if (!products || !products.length) return null;

    const product = products.find(
      ({ Name, EAN }) => `${Name} (${EAN})` === productName
    );
    return product && product.EAN || null;
  };

  const [yearToYearMachinesSalesFilter, setYearToYearMachinesSalesFilter] =
    useState(getDefaultFilters("yearToYearMachinesSalesFilter") || {});

  const [yearToYearProductsSalesFilter, setYearToYearProductsSalesFilter] =
    useState(getDefaultFilters("yearToYearProductsSalesFilter") || {});

  const filterTerminalsCount = (terminals) => {
    const counts = {
      lastHourTerminalsCount: 0,
      upTo72HoursTerminalsCount: 0,
      inactiveTerminalsCount: 0,
    };
    terminals &&
      terminals.forEach(({ LastTrxCreateDateTimeInHour }) => {
        if (
          !LastTrxCreateDateTimeInHour ||
          isNaN(!LastTrxCreateDateTimeInHour) ||
          +LastTrxCreateDateTimeInHour > 72
        ) {
          counts.inactiveTerminalsCount++;
          return;
        }
        if (+LastTrxCreateDateTimeInHour < 24) {
          counts.lastHourTerminalsCount++;
          return;
        }
        if (
          +LastTrxCreateDateTimeInHour >= 24 &&
          +LastTrxCreateDateTimeInHour < 72
        ) {
          counts.upTo72HoursTerminalsCount++;
          return;
        }
      });

    return counts;
  };

  const getYearToYearSalesData = async () =>
    validFilter(yearToYearMachinesSalesFilter)
      .then(() =>
        fetchMssqlApi(
          "stat/year-to-year-machines-sales",
          {
            method: "POST",
            data: {
              ...yearToYearMachinesSalesFilter,
              machineSerialNo: getMachineSerialNo(
                yearToYearMachinesSalesFilter
              ),
            },
            hideNotification: true,
          },
          setYearToYearMachinesSales,
          () => {}
        )
      )
      .catch(() => {});

  const getYearToYearProductsSalesData = async () => {
    if (!yearToYearProductsSalesFilter) return;

    return validFilter(yearToYearProductsSalesFilter)
      .then(() =>
        fetchMssqlApi(
          "stat/year-to-year-products-sales",
          {
            method: "POST",
            data: {
              ...yearToYearProductsSalesFilter,
              EAN: getEAN(yearToYearProductsSalesFilter),
            },
            hideNotification: true,
          },
          setYearToYearProductsSales,
          () => {}
        )
      )
      .catch(() => {});
  };

  const getTerminalsActivity = async () =>
    fetchMssqlApi("/stat/terminals", {}, setTerminalsActivity, () => {});

  const handleClickTerminalButton = (evt) => {
    const name = evt.currentTarget.closest("label").getAttribute("name");
    const terminalsActivityCaseTypes = new Map([
      ["lastHourTerminalsCount", 1],
      ["upTo72HoursTerminalsCount", 2],
      ["inactiveTerminalsCount", 3],
    ]);

    const terminalsPath = new URL(`${window.location.origin}/terminals`);
    terminalsPath.searchParams.set(
      "filterType",
      terminalsActivityCaseTypes.get(name)
    );

    window.open(terminalsPath.toString(), "_blank");
  };

  const getMachinesList = async () =>
    fetchMssqlApi("machines-list", {}, (machines) => setMachinesList(machines));
  const getProductsList = async () =>
    fetchMssqlApi('products', { method: "POST", hideNotification: true, data: {}}, response => setProducts(response))

  useEffect(() => {
    try {
      const prevValues = JSON.parse(
        localStorage.getItem("statFilters") || "{}"
      );
      localStorage.setItem(
        "statFilters",
        JSON.stringify({
          ...prevValues,
          yearToYearMachinesSalesFilter,
          yearToYearProductsSalesFilter,
        })
      );
    } catch (e) {
      console.log(e);
    }
  }, [
    yearToYearMachinesSalesFilter,
    yearToYearProductsSalesFilter,
  ]);

  const getInitData = async () => {
    getMachinesList();
    getProductsList();
    currentChartType === "YearToYearMachinesSalesBar" &&
      getYearToYearSalesData();
    currentChartType === "YearToYearProductsSalesBar" &&
      getYearToYearProductsSalesData();
    getTerminalsActivity();
  };

  useEffect(() => {
    getInitData();
  }, []);

  useEffect(() => {
    currentChartType === "YearToYearProductsSalesBar" &&
      getYearToYearProductsSalesData();
  }, [currentChartType, yearToYearProductsSalesFilter.productType]);
  useEffect(() => {
    currentChartType === "YearToYearMachinesSalesBar" &&
      getYearToYearSalesData();
  }, [currentChartType, yearToYearMachinesSalesFilter.paymentType]);

  const yearToYearProductsSalesElement = () => (
    <div>
      <YearToYearProductsSalesBarFilter
        yearToYearFilter={yearToYearProductsSalesFilter || {}}
        handleChangeProductType={(evt) => {
          const value = evt.target.value;
          setYearToYearProductsSalesFilter((prev) => ({
            ...(prev || {}),
            productType: value || null,
          }));
        }}
        handleSubmit={getYearToYearProductsSalesData}
        handleChangeInput={yearToYearProductsSalesChangeInput}
        products={products}
        getData={getYearToYearProductsSalesData}
        isVisible={yearToYearProductsSalesFilter && yearToYearProductsSalesFilter.IsVisible || false}
        setIsVisible={() =>
          setYearToYearProductsSalesFilter((prev) => ({
            ...(prev || {}),
            IsVisible: !(prev && prev.IsVisible || false),
          }))
        }
      />
        <YearToYearProductsSalesBar
          dataset1={
            yearToYearProductsSales &&
            yearToYearProductsSales.map(
              ({ PrevSummaryIncomeValue }) => +PrevSummaryIncomeValue
            )
          }
          dataset2={
            yearToYearProductsSales &&
            yearToYearProductsSales.map(
              ({ CurrentSummaryIncomeValue }) => +CurrentSummaryIncomeValue
            )
          }
          datasetCount1={
            yearToYearProductsSales &&
            yearToYearProductsSales.map(
              ({ PrevSummaryIncomeCount }) => +PrevSummaryIncomeCount
            )
          }
          datasetCount2={
            yearToYearProductsSales &&
            yearToYearProductsSales.map(
              ({ CurrentSummaryIncomeCount }) => +CurrentSummaryIncomeCount
            )
          }
          labels={
            yearToYearProductsSales &&
            yearToYearProductsSales.map(({ PrevMonth, CurrentMonth }) => months[Number(PrevMonth || CurrentMonth) -1 ])
          }
          firstBarKey={
            yearToYearProductsSales.find(({ PrevYear }) => PrevYear) && yearToYearProductsSales.find(({ PrevYear }) => PrevYear).PrevYear 
          }
          secondBarKey={
            yearToYearProductsSales.find(({ CurrentYear }) => CurrentYear) && 
            yearToYearProductsSales.find(({ CurrentYear }) => CurrentYear).CurrentYear
          }
          handleChangeInput={yearToYearProductsSalesChangeInput}
        />
    </div>
  );

  const yearToYearMachinesSalesElement = () => (
    <div>
      <YearToYearMachinesSalesBarFilter
        yearToYearFilter={yearToYearMachinesSalesFilter || {}}
        handleChangePaymentType={(evt) => {
          const value = evt.target.value;
          setYearToYearMachinesSalesFilter((prev) => ({
            ...prev,
            paymentType: value,
          }));
        }}
        handleSubmit={getYearToYearSalesData}
        handleChangeInput={yearToYearChangeInput}
        machines={machinesList}
        getData={getYearToYearSalesData}
        isVisible={yearToYearMachinesSalesFilter && yearToYearMachinesSalesFilter.IsVisible}
        setIsVisible={() =>
          setYearToYearMachinesSalesFilter((prev) => ({
            ...prev,
            IsVisible: !(prev && prev.IsVisible),
          }))
        }
      />
      <YearToYearMachinesSalesBar
        dataset1={
          yearToYearMachinesSales &&
          yearToYearMachinesSales.map(
            ({ PrevSummaryValue }) => +PrevSummaryValue
          )
        }
        dataset2={
          yearToYearMachinesSales &&
          yearToYearMachinesSales.map(
            ({ CurrentSummaryValue }) => +CurrentSummaryValue
          )
        }
        datasetCount1={
          yearToYearMachinesSales &&
          yearToYearMachinesSales.map(
            ({ PrevSummaryCount }) => +PrevSummaryCount
          )
        }
        datasetCount2={
          yearToYearMachinesSales &&
          yearToYearMachinesSales.map(
            ({ CurrentSummaryCount }) => +CurrentSummaryCount
          )
        }
        labels={
          yearToYearMachinesSales &&
          yearToYearMachinesSales.map(({ PrevMonth }) => months[--PrevMonth])
        }
        firstBarKey={`${
          yearToYearMachinesSales.find(({ PrevYear }) => PrevYear) && 
          yearToYearMachinesSales.find(({ PrevYear }) => PrevYear).PrevYear || ""
        }`}
        secondBarKey={`${
          yearToYearMachinesSales.find(({ CurrentYear }) => CurrentYear) && 
          yearToYearMachinesSales.find(({ CurrentYear }) => CurrentYear).CurrentYear || ""
        }`}
        handleChangeInput={yearToYearChangeInput}
      />
    </div>
  );

const GetChartListElement = ({ name, value }, idx) => {
  const fontSize =
    window.innerWidth >= 1920 && window.innerHeight <= 1080
      ? "13px"
      : "9px";

  return (
    <li className="nav-item" key={idx}>
      <button
        className={`nav-link ${currentChartType === value && "active"}`}
        onClick={() => currentChartType !== value && setCurrentChartType(value)}
        tabIndex="0"
        style={{ fontSize }}
      >
        {name}
      </button>
    </li>
  );
};

  const getChartsByType = (type) => {
    switch (type) {
      case "CurrentSales":
        return (
          <div className="container-fluid" style={{ minWidth: 0 }}>
            <div className="row" name="sales-chart">
              <div className="col-12 col-md-12 col-lg-8">
                <div style={containerStyles}>
                  <ValueSalesChart machines={machinesList} />
                </div>
              </div>
              {terminalsActivity && (
                <TerminalsInfo
                  handleSearchClick={handleSearchClick}
                  lastHourTerminalsCount={
                    filterTerminalsCount(terminalsActivity)
                      .lastHourTerminalsCount
                  }
                  upTo72HoursTerminalsCount={
                    filterTerminalsCount(terminalsActivity)
                      .upTo72HoursTerminalsCount
                  }
                  inactiveTerminalsCount={
                    filterTerminalsCount(terminalsActivity)
                      .inactiveTerminalsCount
                  }
                  handleClick={handleClickTerminalButton}
                />
              )}
            </div>
          </div>
        );
      case "SalesByDay":
        return (
          <SalesByDayChart machines={machinesList}/>
        );
      case "ZeroSales":
        return (
          <ZeroSalesChart machines={machinesList} products={products} />
        );
      case "UnassignedChoices":
        return (
          <UnassignedChoicesChart machines={machinesList} products={products} />
        );
      case "ProductPriceDeviations":
        return <ProductPriceDeviationsChart />;
      case "MachinesSalesCharts":
        return (
          <MachinesSalesCharts
            showMachinesStat={true}
            setShowMachinesStat={() => {}}
          />
        );
      case "ProductsSalesCharts":
        return (
          <ProductsSalesCharts
            showProductsStat={true}
            setShowProductsStat={() => {}}
          />
        );
      case "YearMachinesSalesChart":
        return (
          <YearMachinesSalesChart
            showMachinesStat={true}
            setShowMachinesStat={() => {}}
          />
        );
      case "YearProductsSalesChart":
        return (
          <YearProductsSalesChart
            showProductsStat={true}
            setShowProductsStat={() => {}}
          />
        );
      case "YearToYearMachinesSalesBar":
        return yearToYearMachinesSalesElement();
      case "YearToYearProductsSalesBar":
        return yearToYearProductsSalesElement();
    }
  };

  const months = [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień",
  ];

  const yearToYearChangeInput = (evt) => {
    const machineName = evt && evt.target && evt.target.value || "";
    setYearToYearMachinesSalesFilter((prev) => ({
      ...prev,
      machineName,
    }));
    const serialNo = getMachineSerialNo({ machineName });
    if (serialNo) {
      validFilter({ ...yearToYearMachinesSalesFilter, machineName }).then(() =>
        fetchMssqlApi(
          "stat/year-to-year-machines-sales",
          {
            method: "POST",
            data: {
              ...yearToYearMachinesSalesFilter,
              machineName,
              machineSerialNo: serialNo,
            },
            hideNotification: true,
          },
          setYearToYearMachinesSales,
          (err) => console.log(err)
        )
      );
    }
  };

  const yearToYearProductsSalesChangeInput = (evt) => {
    const productName = evt && evt.target && evt.target.value || "";
    setYearToYearProductsSalesFilter((prev) => ({
      ...(prev || {}),
      productName,
    }));
    const EAN = getEAN({ productName });
    if (EAN && yearToYearProductsSalesFilter) {
      validFilter({ ...yearToYearProductsSalesFilter, productName }).then(() =>
        fetchMssqlApi(
          "stat/year-to-year-products-sales",
          {
            method: "POST",
            data: {
              ...yearToYearProductsSalesFilter,
              EAN,
            },
            hideNotification: true,
          },
          setYearToYearProductsSales,
          (err) => console.log(err)
        )
      );
    }
  };
  const containerStyles = {
    position: "relative",
    minHeight: "10vh",
  };

  const handleSearchClick = (evt) => {
    const name = evt.currentTarget.closest("div").getAttribute("name");

    const terminalsActivityCaseTypes = new Map([
      ["lastHourTerminalsCount", 1],
      ["upTo72HoursTerminalsCount", 2],
      ["inactiveTerminalsCount", 3],
    ]);

    setShowModal(terminalsActivityCaseTypes.get(name));
  };

  const statIsEnable =
    useContext(NavigationContext)?.state?.permissions?.findIndex(
      ({ Name }) => Name === "VD_STAT"
    ) !== -1;

  return statIsEnable ? (
    <div>
      <div>
        <div className="card">
          <div className="card-header pb-2 pt-2 pr-3 pl-3">
            <ul className="nav nav-tabs machine-tabs">
              {chartTypes.map(GetChartListElement)}
            </ul>
          </div>
          <div className="card-body p-2 ">
            {getChartsByType(currentChartType)}
          </div>
        </div>
      </div>

      {showModal && (
        <FilteredMachineSales
          header={(() => {
            switch (showModal) {
              case 1:
                return "";
              case 2:
                return "Brak transakcji od ponad 24h";
              case 3:
                return "Brak transakcji od ponad 72h";
            }
          })()}
          onHide={hideModal}
          showModal={Boolean(showModal)}
          machines={machinesList}
          filterType={showModal}
        />
      )}
    </div>
  ) : (
    <></>
  );
}